import { useId, useState } from "react";
import { C, withAlpha } from "../../constants/theme";
import { fmtMoeda, fmtData } from "../../utils/format";
import { iconeCanal, repassePrevisto, descreverCusto, validarVendaOnline } from "../../utils/vendaOnline";
import { Btn } from "../ui/Btn";
import { ChipsCustos, EmbalagemCampos, CartaoCampos, rotulo, campo, dica, linkBtn } from "./CamposVendaOnline";
import "./vendaOnline.css";
import { AssistenteTaxas } from "./AssistenteTaxas";

/* Tela da venda online no pedido (a conta mora em utils/vendaOnline.js). */

const grade = { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:10 };

function Campo({ label, nota, ...p }) {
  const id = useId();
  return (
    <div style={{ display:"flex", flexDirection:"column", minWidth:0 }}>
      <label htmlFor={id} style={rotulo}>{label}</label>
      <input id={id} {...p} style={{ ...campo, ...p.style }} />
      {nota && <div style={dica}>{nota}</div>}
    </div>
  );
}

function Bloco({ titulo, children }) {
  return (
    <div className="order-sale-block">
      <h4>{titulo}</h4>
      {children}
    </div>
  );
}

function Linha({ texto, valor, sinal = "", cor, sempre = false, forte = false }) {
  if (!sempre && !valor) return null;
  return (
    <div style={{ display:"flex", justifyContent:"space-between", gap:10, fontSize:forte ? 14 : 12, color:forte ? C.text : C.muted, fontWeight:forte ? 900 : 400, padding:"3px 0" }}>
      <span>{texto}</span><span style={{ color:cor || C.text, fontWeight:forte ? 900 : 700, whiteSpace:"nowrap" }}>{sinal}{fmtMoeda(valor)}</span>
    </div>
  );
}

export function descreverFaixa(f) {
  if (!f) return "";
  const fixo = Number(f.valorFixo) || 0;
  const extra = f.tipoFixo === "pct"
    ? (fixo > 0 ? ` + ${fixo}% do valor` : "")
    : (fixo > 0 ? ` + ${fmtMoeda(fixo)} por unidade` : "");
  return `${Number(f.comissao) || 0}%${extra}`;
}

const nomeTaxa = (canal) => canal?.marketplace ? canal.nome : "plataforma";
const descreverCartao = (cartao) => cartao === "debito" ? "débito" : cartao ? `crédito ${cartao}x` : "";

export function SeletorCanal({ canais, valor, onChange }) {
  return (
    <div className="order-sale-channels" role="radiogroup" aria-label="Onde vendeu?">
      {canais.map(c => {
        const ativo = c.id === valor;
        return (
          <button key={c.id} type="button" role="radio" aria-checked={ativo} onClick={() => onChange(c.id)}>
            <span aria-hidden="true">{c.icone}</span> {c.nome}
          </button>
        );
      })}
    </div>
  );
}

/* Campos + conta da venda online no formulário do pedido.
   - venda / onChange: estado do formulário (VENDA_VAZIA) e patch dele
   - calc: resultado de calcularVendaOnline
   - catalogo / onCriarCusto: custos salvos e criação (que já salva na lista)
   - materiais / embalagem / onEmbalagem: cadastro de Materiais, itens de
     envio do pedido ({ itens, total }) e troca deles
   - custoProducao: custo do pedido sem a embalagem
   - onSalvarPadrao / temPadrao: guardar custos, embalagem e cartão como
     padrão desta loja */
export function VendaOnlineSecao({
  venda, onChange, calc, canal, maquininhas, tabela,
  catalogo = [], onCriarCusto, materiais = [], embalagem = { itens:[], total:0 }, onEmbalagem,
  custoProducao = 0, onSalvarPadrao, temPadrao = false,
  comparacaoTaxas, onAplicarPreco,
}) {
  const [salvandoPadrao, setSalvandoPadrao] = useState(false);
  if (!calc) return null;
  const set = (nome) => (e) => onChange({ [nome]: e.target.value });
  const taxaNome = nomeTaxa(canal);
  const lucro = calc.sobra - embalagem.total - custoProducao;
  const erros = validarVendaOnline(venda, calc);
  const fechada = !!venda.repasse_em;

  let notaTaxa = null;
  if (!calc.taxaFixada) {
    notaTaxa = calc.faixa
      ? <>Pela tabela {tabela?.origem === "oficial" ? "oficial" : "embutida"}: {descreverFaixa(calc.faixa)}. Estimativa por unidade; confira o valor real no extrato da loja.</>
      : canal?.marketplace ? "Sem tabela pra essa loja — digite a taxa." : "Comissão de site/app de venda, se houver.";
  } else if (calc.loja) {
    const difere = Math.abs(calc.taxaPlataforma - calc.taxaPlataformaTabela) > 0.005;
    notaTaxa = <>
      {venda.taxa_manual ? "Valor digitado." : `Valor salvo na venda${calc.faixa ? ` (${descreverFaixa(calc.faixa)})` : ""}.`}
      {(difere || venda.taxa_manual) && <> <button type="button" style={linkBtn} onClick={() => onChange({ taxa_plataforma:"", taxa_manual:false, taxa_ref:null })}>↻ Usar a tabela de hoje ({fmtMoeda(calc.taxaPlataformaTabela)})</button></>}
    </>;
  }

  const salvarPadrao = async () => {
    setSalvandoPadrao(true);
    try { await onSalvarPadrao(); } finally { setSalvandoPadrao(false); }
  };

  return (
    <section className="order-sale" aria-label={`Venda ${canal?.nome || "online"}`}>
      <div className="order-sale-heading">
        <div><span className="order-sale-eyebrow">{canal?.icone} VENDA ONLINE · {(canal?.nome || "").toUpperCase()}</span><h3>Da venda ao lucro</h3><p>Confira o que o cliente paga, o que a loja retém e o que sobra para você.</p></div>
        {onSalvarPadrao && (
          <button type="button" onClick={salvarPadrao} disabled={salvandoPadrao}
            title="Guarda os custos, a embalagem e o cartão deste pedido — entram sozinhos nos próximos pedidos desta loja"
            style={{ background:"none", border:"1px solid "+withAlpha(C.accent, "55"), borderRadius:8, color:C.accent, fontSize:11, fontWeight:700, padding:"5px 10px", cursor:"pointer" }}>
            {salvandoPadrao ? "Salvando…" : `⭐ ${temPadrao ? "Atualizar" : "Salvar como"} padrão ${canal?.marketplace ? "da " + canal.nome : "de " + (canal?.nome || "")}`}
          </button>
        )}
      </div>
      {temPadrao && <div style={{ ...dica, marginTop:-10 }}>Padrão de {canal?.nome} carregado — ajuste o que for diferente neste pedido.</div>}
      {fechada && <div className="order-sale-notice">Repasse confirmado. Os valores desta venda estão protegidos; estorne o recebimento para corrigi-los.</div>}
      {erros.length > 0 && <div className="order-sale-errors" role="alert">{erros.map(erro => <p key={erro}>{erro}</p>)}</div>}
      {onAplicarPreco && <AssistenteTaxas comparacao={comparacaoTaxas} precoAtual={calc.precoProdutos} onAplicar={onAplicarPreco} disabled={fechada} />}
      <div className="order-sale-layout">
      <fieldset disabled={fechada} className="order-sale-fields">
      <Bloco titulo="1. Pedido e entrega">
      <div style={grade}>
        <Campo label="Nº DO PEDIDO NA LOJA" value={venda.numero_loja} onChange={set("numero_loja")} placeholder="opcional" />
        <Campo label="FRETE COBRADO DO CLIENTE (R$)" type="number" min="0" step="0.01" inputMode="decimal" value={venda.frete_cliente} onChange={set("frete_cliente")} placeholder="0,00" />
        {canal?.marketplace
          ? <Campo label="FRETE QUE A LOJA DESCONTA (R$)" type="number" min="0" step="0.01" inputMode="decimal" value={venda.frete_vendedor} onChange={set("frete_vendedor")} placeholder="0,00"
              nota="Sua parte no frete grátis — sai do repasse." />
          : <Campo label="FRETE QUE VOCÊ PAGA (R$)" type="number" min="0" step="0.01" inputMode="decimal" value={venda.frete_vendedor} onChange={set("frete_vendedor")} placeholder="0,00"
              nota="Correios/transportadora — pago à parte." />}
      </div>
      {canal?.marketplace && (
        <div style={dica}>Preencha o frete do cliente apenas quando ele fizer parte do seu repasse. O frete pago diretamente ao marketplace não entra como receita sua.</div>
      )}
      </Bloco>
      <Bloco titulo="2. Taxas da venda">
      <span className="order-sale-tag">{calc.taxaFixada ? venda.taxa_manual ? "Taxa informada" : "Taxa salva neste pedido" : "Taxa estimada"}</span>
      <div style={grade}>
        <Campo label={`TAXA ${taxaNome.toUpperCase()} (R$)`} type="number" min="0" step="0.01" inputMode="decimal"
          value={venda.taxa_plataforma} placeholder={calc.taxaPlataformaTabela.toFixed(2)}
          onChange={e => onChange({ taxa_plataforma:e.target.value, taxa_manual:e.target.value !== "", taxa_ref:null })}
          nota={notaTaxa} />
      </div>
      <details className="order-sale-extra" open={!!venda.maquininha_id || Number(venda.cartao_pct) > 0}>
      <summary>{canal?.marketplace ? "Cartão cobrado à parte pela loja" : "Cartão ou gateway de pagamento"}</summary>
      <p className="order-sale-help">{canal?.marketplace ? "Use somente se essa cobrança não estiver incluída na comissão, para não descontar a mesma taxa duas vezes." : "Selecione a maquininha para calcular a taxa ou informe o percentual do seu gateway."}</p>
        <CartaoCampos valor={venda} onChange={onChange} maquininhas={maquininhas} marketplace={!!canal?.marketplace} calc={calc} />
      </details>
      </Bloco>

      {onEmbalagem && (
        <Bloco titulo="3. Embalagem e envio">
          <EmbalagemCampos materiais={materiais} itens={embalagem.itens} onChange={onEmbalagem} />
        </Bloco>
      )}

      <Bloco titulo="Custos adicionais">
        <p className="order-sale-help">Valores pagos por você, além do que a plataforma retém.</p>
        <ChipsCustos catalogo={catalogo} ativos={venda.custos || []} onChange={custos => onChange({ custos })} onCriar={onCriarCusto} />
      </Bloco>
      </fieldset>
      <aside className="order-sale-summary" aria-label="Resultado da venda">
        <span className="order-sale-eyebrow">RESULTADO DESTE PEDIDO</span>
        <div className="order-sale-total"><span>Total pago pelo cliente</span><strong>{fmtMoeda(calc.bruto)}</strong></div>
        <Linha texto="Preço dos produtos" valor={calc.precoProdutos} sempre />
        <Linha texto="+ Frete cobrado do cliente" valor={calc.freteCliente} />
        <Linha texto="+ Taxa do cartão paga pelo cliente" valor={calc.acrescimoCartao} />
        <Linha texto={`− Taxa ${taxaNome}`} valor={calc.taxaPlataforma} cor={C.warning} />
        <Linha texto={`− Cartão (${calc.cartaoPct.toFixed(2)}%)`} valor={calc.taxaCartao} cor={C.warning} />
        <Linha texto="− Frete descontado pela loja" valor={calc.freteRetido} cor={C.warning} />
        {Math.abs(calc.ajuste) > 0.005 && (
          <Linha texto={calc.ajuste > 0 ? "− Diferença pro valor da loja" : "+ Diferença pro valor da loja"} valor={Math.abs(calc.ajuste)} cor={calc.ajuste > 0 ? C.warning : C.success} />
        )}
        <div style={{ borderTop:"1px solid "+C.border, margin:"6px 0", paddingTop:8 }}>
          <Campo label={canal?.marketplace ? "VOCÊ RECEBE DA LOJA (R$)" : "CAI NA SUA CONTA (R$)"} type="number" step="0.01" inputMode="decimal"
            min="0" max={calc.bruto} disabled={fechada}
            value={venda.liquido_informado} placeholder={calc.liquidoCalculado.toFixed(2)} onChange={set("liquido_informado")}
            style={{ color:C.success, fontWeight:900, fontSize:16 }}
            nota={calc.liquidoInformado
              ? <>Valor que a loja mostrou — é ele que vale. <button type="button" disabled={fechada} style={linkBtn} onClick={() => onChange({ liquido_informado:"" })}>Usar a conta ({fmtMoeda(calc.liquidoCalculado)})</button></>
              : "Pela conta acima. Se a loja mostrar outro \"você recebe\", digite aqui — ele é que vale."} />
          {calc.liquido > calc.bruto + 0.005 && (
            <div style={{ ...dica, color:C.warning }}>⚠️ Maior que o valor pago pelo cliente ({fmtMoeda(calc.bruto)}) — confira.</div>
          )}
        </div>
        <Linha texto="− Frete pago à parte" valor={calc.freteAParte} cor={C.warning} />
        {calc.custos.map((c, i) => (
          <Linha key={c.id || i} texto={`− ${c.nome} (${descreverCusto(c)})`} valor={c.total} cor={C.warning} />
        ))}
        <Linha texto={`− Embalagem${embalagem.itens.length ? ` (${embalagem.itens.map(i => `${i.nome} ×${i.qtd}`).join(", ")})` : ""}`} valor={embalagem.total} cor={C.warning} />
        <Linha texto="− Custo de produção" valor={custoProducao} cor={C.warning} />
        <div className={`order-sale-profit ${lucro < 0 ? "is-negative" : ""}`}>
          <Linha texto="= Lucro" valor={lucro} cor={lucro >= 0 ? C.success : C.danger} sempre forte />
          <p>{calc.bruto > 0 ? `${(lucro / calc.bruto * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}% de margem sobre a venda` : "Lucro após taxas, envio, embalagem e produção"}</p>
        </div>
        <p className="order-sale-help">{calc.liquidoInformado ? "Repasse informado pelo extrato." : "Repasse estimado. Confirme o depósito quando ele chegar."} A embalagem já está incluída uma única vez neste resultado.</p>
      </aside>
      </div>
    </section>
  );
}

/* Quadro da venda online no detalhe do pedido — lê só a foto salva. */
export function ResumoVendaOnline({ vo }) {
  if (!vo?.canal) return null;
  const nome = vo.canal_nome || "loja";
  const item = (texto, valor, sinal, cor, key) => (
    <div key={key} className="quote-metric" style={{ marginBottom:8 }}><span>{texto}</span><strong style={cor ? { color:cor } : undefined}>{sinal}{fmtMoeda(valor)}</strong></div>
  );
  const ajuste = Number(vo.ajuste) || 0;
  // Pedidos salvos antes da separação retido × à parte guardavam só frete_vendedor
  const freteRetido = vo.frete_retido ?? (vo.frete_a_parte == null ? vo.frete_vendedor : 0);
  const custosAParte = Number(vo.custos_a_parte) || 0;
  return (
    <div className="commerce-section">
      <h3>{iconeCanal(vo.canal)} Venda {nome}{vo.numero_loja ? ` · nº ${vo.numero_loja}` : ""}</h3>
      {item("Preço dos produtos", vo.preco_produtos, "")}
      {vo.frete_cliente > 0 && item("Frete cobrado do cliente", vo.frete_cliente, "+ ")}
      {vo.acrescimo_cartao > 0 && item("Taxa do cartão paga pelo cliente", vo.acrescimo_cartao, "+ ")}
      {vo.taxa_plataforma > 0 && item(`Taxa ${nome}${vo.taxa_ref ? ` (${descreverFaixa(vo.taxa_ref)})` : ""}`, vo.taxa_plataforma, "− ", C.warning)}
      {vo.taxa_cartao > 0 && item(`Cartão ${[vo.maquininha_nome, descreverCartao(vo.cartao)].filter(Boolean).join(" ")} (${Number(vo.cartao_pct || 0).toFixed(2)}%)`, vo.taxa_cartao, "− ", C.warning)}
      {freteRetido > 0 && item("Frete descontado pela loja", freteRetido, "− ", C.warning)}
      {Math.abs(ajuste) > 0.005 && item("Diferença pro valor da loja", Math.abs(ajuste), ajuste > 0 ? "− " : "+ ", ajuste > 0 ? C.warning : C.success)}
      <div className="quote-metric" style={{ marginBottom:8, borderTop:"1px solid "+C.border, paddingTop:8 }}>
        <span>Você recebe da loja</span><strong style={{ color:C.success }}>{fmtMoeda(vo.liquido)}</strong>
      </div>
      {vo.frete_a_parte > 0 && item("Frete pago à parte", vo.frete_a_parte, "− ", C.warning)}
      {(vo.custos || []).map((c, i) => item(`${c.nome} (${descreverCusto(c)})`, c.total, "− ", C.warning, c.id || i))}
      {custosAParte > 0 && (
        <div className="quote-metric" style={{ marginBottom:0, borderTop:"1px solid "+C.border, paddingTop:8 }}>
          <span>Sobra da venda</span><strong>{fmtMoeda((Number(vo.liquido) || 0) - custosAParte)}</strong>
        </div>
      )}
      {vo.repasse_em && <p className="commerce-hint">Repasse confirmado em {fmtData(vo.repasse_em)}.{vo.custos_no_caixa ? " Custos à parte lançados no caixa." : ""}</p>}
    </div>
  );
}

/* Confirma que a loja/gateway depositou: o que caiu entra no caixa, o resto
   fica como retido pela loja (ver montarRepasse). Os custos pagos à parte
   (frete dos Correios, etiqueta…) podem ir pro caixa como saída no mesmo clique. */
export function ModalRepasse({ pedido, saldo, enviando, onConfirmar, onFechar }) {
  const vo = pedido.venda_online || {};
  const nome = vo.canal_nome || "loja";
  const [valor, setValor] = useState(() => repassePrevisto(pedido).toFixed(2));
  const custosAParte = Number(vo.custos_a_parte) || 0;
  const podeLancar = custosAParte > 0 && !vo.custos_no_caixa;
  const [lancarCustos, setLancarCustos] = useState(true);
  const caiu = Number(valor);
  const valorValido = valor.trim() !== "" && Number.isFinite(caiu) && caiu >= 0 && caiu <= saldo + 0.005;
  const retido = Math.max(0, saldo - caiu);
  const fechar = () => { if (!enviando) onFechar(); };
  return (
    <>
      <div onClick={fechar} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)", zIndex:1010 }} />
      <div role="dialog" aria-modal="true" aria-label="Confirmar repasse" onKeyDown={e => { if (e.key === "Escape") { e.stopPropagation(); fechar(); } }}
        style={{ position:"fixed", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:"min(420px,95vw)", background:C.panel, border:"1px solid "+withAlpha(C.accent, "55"), borderRadius:18, padding:22, boxSizing:"border-box", zIndex:1011, boxShadow:"0 24px 64px rgba(0,0,0,0.7)", maxHeight:"92vh", overflowY:"auto" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:14 }}>
          <div>
            <div style={{ fontWeight:900, fontSize:15, color:C.accent, letterSpacing:1 }}>{iconeCanal(vo.canal)} CONFIRMAR REPASSE</div>
            <div style={{ color:C.muted, fontSize:12, marginTop:4 }}>{pedido.cliente} · {nome}{vo.numero_loja ? ` · nº ${vo.numero_loja}` : ""}</div>
          </div>
          <button aria-label="Fechar" disabled={enviando} onClick={fechar} style={{ background:"none", border:"none", color:C.muted, cursor:"pointer", fontSize:20, lineHeight:1 }}>✕</button>
        </div>

        <div style={{ background:C.card, border:"1px solid "+C.border, borderRadius:10, padding:"8px 12px", marginBottom:14 }}>
          <Linha texto="Falta quitar do pedido" valor={saldo} sempre />
          <Linha texto="Previsto que a loja desconta" valor={Math.max(0, saldo - repassePrevisto(pedido))} sinal="− " cor={C.warning} sempre />
        </div>

        <label htmlFor="valor-repasse" style={rotulo}>QUANTO CAIU PRA VOCÊ (R$)</label>
        <input id="valor-repasse" autoFocus type="number" min="0" max={saldo} step="0.01" inputMode="decimal" value={valor} onChange={e => setValor(e.target.value)}
          style={{ ...campo, color:C.success, fontSize:18, fontWeight:900, padding:"10px 14px" }} />
        <div style={{ ...dica, marginBottom:14 }}>
          {nome} fica com <strong style={{ color:C.warning }}>{fmtMoeda(retido)}</strong>. Só o que caiu entra no caixa; o que a loja reteve quita o resto do pedido sem passar pelo caixa. Se caiu diferente do previsto, o lucro do pedido é corrigido.
        </div>

        {!valorValido && <p role="alert" style={{color:C.danger,fontSize:12}}>Informe um valor entre zero e {fmtMoeda(saldo)}.</p>}
        {podeLancar && (
          <label style={{ display:"flex", gap:8, alignItems:"flex-start", fontSize:12, color:C.text, marginBottom:16, cursor:"pointer", lineHeight:1.5 }}>
            <input type="checkbox" checked={lancarCustos} onChange={e => setLancarCustos(e.target.checked)} style={{ marginTop:2 }} />
            <span>Lançar também a saída de <strong style={{ color:C.warning }}>{fmtMoeda(custosAParte)}</strong> no caixa (frete e custos que você pagou à parte)</span>
          </label>
        )}

        <Btn onClick={() => { if (valorValido) onConfirmar(caiu, podeLancar && lancarCustos); }} disabled={enviando || !valorValido} color={C.success} style={{ width:"100%" }}>
          {enviando ? "Registrando…" : `✅ Confirmar repasse de ${fmtMoeda(caiu)}`}
        </Btn>
      </div>
    </>
  );
}
